import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { supabase } from '../lib/supabaseClient';
import { useSnackbar } from 'notistack';

function DriverNotificationModal({ isOpen, onClose, driver }) {
  const { enqueueSnackbar } = useSnackbar();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customNotification, setCustomNotification] = useState({
    title: '',
    body: ''
  });

  // Fetch notification templates when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchNotificationTemplates();
    }
  }, [isOpen]);

  // Update custom notification when a template is selected
  useEffect(() => {
    if (selectedTemplate) {
      setCustomNotification({
        title: selectedTemplate.title,
        body: selectedTemplate.body
      });
    }
  }, [selectedTemplate]);

  const fetchNotificationTemplates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('notification_templates')
        .select('*')
        .eq('active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error fetching notification templates:', error);
      enqueueSnackbar('Şablonlar yüklenirken bir hata oluştu', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomNotification(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const sendNotification = async () => {
    if (!customNotification.title || !customNotification.body) {
      enqueueSnackbar('Başlık ve içerik alanları doldurulmalıdır', { variant: 'warning' });
      return;
    }

    try {
      setSendingNotification(true);

      // Fetch driver's push token
      const { data: driverData, error: driverError } = await supabase
        .from('profiles')
        .select('push_token')
        .eq('id', driver.id)
        .single();

      if (driverError) throw driverError;

      if (!driverData?.push_token) {
        enqueueSnackbar('Sürücünün bildirim token\'ı bulunamadı', { variant: 'warning' });
        return;
      }

      // Prepare notification message
      const message = {
        to: driverData.push_token,
        sound: 'default',
        title: customNotification.title,
        body: customNotification.body,
        data: { type: 'custom_notification', driverId: driver.id },
      };

      // Send notification via Expo's Push API
      await fetch('https://exp.host/--/api/v2/push/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        mode: 'no-cors',
        body: JSON.stringify(message),
      });

      enqueueSnackbar('Bildirim başarıyla gönderildi', { variant: 'success' });
      onClose();
    } catch (error) {
      console.error('Error sending notification:', error);
      enqueueSnackbar('Bildirim gönderilirken bir hata oluştu', { variant: 'error' });
    } finally {
      setSendingNotification(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 text-center">
        <div className="inline-block w-full max-w-md my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">
              Bildirim Gönder: {driver?.first_name} {driver?.last_name}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* Modal Body */}
          <div className="p-4">
            {/* Template Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bildirim Şablonu Seç
              </label>
              {loading ? (
                <div className="text-center py-2">Şablonlar yükleniyor...</div>
              ) : templates.length === 0 ? (
                <div className="text-center py-2 text-gray-500">Şablon bulunamadı</div>
              ) : (
                <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      onClick={() => handleTemplateSelect(template)}
                      className={`p-2 border rounded cursor-pointer hover:bg-gray-50 ${
                        selectedTemplate?.id === template.id ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                      }`}
                    >
                      <div className="font-medium">{template.title}</div>
                      <div className="text-sm text-gray-600 truncate">{template.body}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Custom Notification */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bildirim İçeriği
              </label>
              <div className="mb-3">
                <label className="block text-xs text-gray-500 mb-1">Başlık</label>
                <input
                  type="text"
                  name="title"
                  value={customNotification.title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Bildirim başlığı"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">İçerik</label>
                <textarea
                  name="body"
                  value={customNotification.body}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Bildirim içeriği"
                ></textarea>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={onClose}
                className="mr-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                İptal
              </button>
              <button
                type="button"
                onClick={sendNotification}
                disabled={sendingNotification}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
              >
                <FontAwesomeIcon icon={faPaperPlane} className="mr-2" />
                {sendingNotification ? 'Gönderiliyor...' : 'Gönder'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DriverNotificationModal;
